import { useState, useEffect, useRef } from 'react';
import { X, FileText, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { ModalHeader } from './ModalHeader';
import { useTranslation } from 'react-i18next';

type SignerRole = 'CLIENT' | 'LEGAL_GUARDIAN' | 'PRACTITIONER' | 'SHOP_MANAGER';

const SIGNER_ROLE_LABELS: Record<SignerRole, string> = {
  CLIENT: 'Client',
  LEGAL_GUARDIAN: 'Représentant légal (tuteur)',
  PRACTITIONER: 'Praticien',
  SHOP_MANAGER: 'Responsable du shop',
};

export interface DocumentSignModalProps {
  documentId: string;
  documentName: string;
  pdfUrl: string;
  /** Prefill the signer name (e.g. from the logged-in user) */
  defaultSignerName?: string;
  /** Restrict role options. Defaults to CLIENT only for frontoffice. */
  availableRoles?: SignerRole[];
  /** Called on successful signature with the server response */
  onSuccess: (result: { documentId: string; signedAt: string; documentHash: string }) => void;
  onClose: () => void;
  /** API endpoint to POST to. Defaults to /api/client-documents/:id/sign */
  signEndpoint?: string;
  /** Auth token key in localStorage */
  authTokenKey?: string;
}

export function DocumentSignModal({
  documentId,
  documentName,
  pdfUrl,
  defaultSignerName = '',
  availableRoles = ['CLIENT'],
  onSuccess,
  onClose,
  signEndpoint,
  authTokenKey = 'client_token',
}: DocumentSignModalProps) {
  const { t } = useTranslation();
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [signerRole, setSignerRole] = useState<SignerRole>(availableRoles[0] ?? 'CLIENT');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Fetch the PDF with auth headers and create a blob URL so the iframe
  // can display it without needing to send Authorization headers directly.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPdfLoading(true);
      setPdfError(false);
      try {
        const token = localStorage.getItem(authTokenKey);
        const res = await fetch(pdfUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error('Failed to load PDF');
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPdfBlobUrl(url);
      } catch {
        if (!cancelled) setPdfError(true);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [pdfUrl, authTokenKey]);

  const endpoint =
    signEndpoint ?? `/api/client-documents/${encodeURIComponent(documentId)}/sign`;

  const canSubmit = signerName.trim().length > 0 && signatureDataUrl !== null && agreed && !loading && !done;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem(authTokenKey);
      const baseUrl = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || '';
      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          signatureImageDataUrl: signatureDataUrl,
          signerName: signerName.trim(),
          signerRole,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Erreur lors de la signature');
      }

      setDone(true);
      onSuccess({
        documentId: data.data.documentId,
        signedAt: data.data.signedAt,
        documentHash: data.data.documentHash,
      });
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/70 backdrop-blur-sm">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="modal-panel w-full max-w-2xl rounded-2xl overflow-hidden">

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100">
            <ModalHeader
              title={
                <div className="flex items-center gap-3 pr-2">
                  <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900">Signature électronique</h2>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{documentName}</p>
                  </div>
                </div>
              }
              onClose={onClose}
              disabled={loading}
            />
          </div>

          {done ? (
            <div className="relative p-10 flex flex-col items-center text-center gap-4">
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                className="modal-close-btn absolute top-3 right-3 p-1.5 rounded-lg text-textSecondary hover:text-textPrimary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Document signé avec succès</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Votre signature a été enregistrée et le document signé vous a été envoyé par email.
              </p>
              <button
                onClick={onClose}
                className="mt-2 px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              {/* PDF Preview */}
              <div className="px-6 pt-5">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Document à signer
                </p>
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center" style={{ height: 260 }}>
                  {pdfLoading && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Chargement du document…</span>
                    </div>
                  )}
                  {!pdfLoading && pdfError && (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">Impossible de charger le document</span>
                    </div>
                  )}
                  {!pdfLoading && !pdfError && pdfBlobUrl && (
                    <iframe
                      src={pdfBlobUrl}
                      title="Document à signer"
                      className="w-full h-full"
                    />
                  )}
                </div>
              </div>

              {/* Form */}
              <div className="px-6 py-5 space-y-4">

                {/* Signer identity */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nom complet du signataire <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Prénom Nom"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  {availableRoles.length > 1 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Rôle du signataire
                      </label>
                      <select
                        value={signerRole}
                        onChange={(e) => setSignerRole(e.target.value as SignerRole)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      >
                        {availableRoles.map((r) => (
                          <option key={r} value={r}>
                            {SIGNER_ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Signature pad */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Votre signature <span className="text-red-500">*</span>
                  </label>
                  <SignaturePad
                    onSign={(dataUrl) => setSignatureDataUrl(dataUrl)}
                    disabled={loading || done}
                  />
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    Je reconnais avoir lu le document ci-dessus dans son intégralité et j'accepte de le signer
                    électroniquement. Cette signature a la même valeur qu'une signature manuscrite conformément
                    à la législation en vigueur.
                  </span>
                </label>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Signer le document
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentSignModal;
